import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';

export function useVisits(workOrderId?: string) {
  const [visits, setVisits] = useState<Database['public']['Tables']['visits']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchVisits() {
      try {
        const query = supabase
          .from('visits')
          .select(`
            *,
            technicians (
              full_name
            )
          `)
          .order('scheduled_date', { ascending: true });

        if (workOrderId) {
          query.eq('work_order_id', workOrderId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;
        setVisits(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch visits');
      } finally {
        setLoading(false);
      }
    }

    fetchVisits();
  }, [workOrderId]);

  const addVisit = async (visitData: Omit<Database['public']['Tables']['visits']['Insert'], 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('visits')
        .insert([visitData])
        .select()
        .single();

      if (error) throw error;
      setVisits(prev => [...prev, data].sort((a, b) => 
        new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime()
      ));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateVisit = async (id: string, visitData: Partial<Database['public']['Tables']['visits']['Update']>) => {
    try {
      const { data, error } = await supabase
        .from('visits')
        .update(visitData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setVisits(prev => prev.map(visit => visit.id === id ? data : visit));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteVisit = async (id: string) => {
    try {
      const { error } = await supabase
        .from('visits')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVisits(prev => prev.filter(visit => visit.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return { visits, loading, error, addVisit, updateVisit, deleteVisit };
}